import { CommonModule, Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, takeUntil } from 'rxjs';
import { AuthPermission } from '../../../manage-users/models/permission';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { MultiselectFilterComponent } from '../../../../shared/components/multiselect-filter/multiselect-filter.component';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { ToastType } from '../../../../shared/interfaces/common';
import {
  ConnectionUser,
  createFormConfig,
  CustomSubscriptionPlan,
  PlanConnectionPayload,
  SubscriptionConnectionListItem,
  UpdateSubscriptionPlanDetails,
} from '../../models/subscription';
import { CommonService } from '../../../../shared/services/common/common.service';
import { PermissionsService } from '../../../../shared/services/permissions/permissions.service';
import { getApiErrorMessage } from '../../../../shared/utils/utils';
import { SubscriptionService } from '../../services/subscription.service';

@Component({
  selector: 'app-create-subscription-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiselectFilterComponent,
    PopupComponent,
  ],
  templateUrl: './create-subscription-plan.component.html',
  styleUrls: ['./create-subscription-plan.component.scss'],
})
export class CreateSubscriptionPlanComponent
  extends MenuComponentBase
  implements OnInit
{
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private subscriptionService = inject(SubscriptionService);
  private commonService = inject(CommonService);
  private permissionService = inject(PermissionsService);
  private location = inject(Location);

  isEditMode = false;
  planNameParam = '';
  selectedPlan: CustomSubscriptionPlan | null = null;

  isLoading = true;
  isSaving = false;

  createForm = createFormConfig();
  permissionList: AuthPermission[] = [];
  connectionsList: SubscriptionConnectionListItem[] = [];

  // Connection Admins
  connectionUsersByName: Record<string, ConnectionUser[]> = {};
  connectionAdminSelections: Record<string, number[]> = {};
  adminPickerConnection: string | null = null;
  tempAdminSelections: number[] = [];
  isLoadingPickerUsers = false;

  private readonly HIDDEN_PERMISSION_CODENAMES: readonly string[] = [
    PermissionCode.MANAGE_SUBSCRIPTION,
    PermissionCode.ADMIN_PRIVILEGES,
    PermissionCode.ACCESS_DNB,
    PermissionCode.SEARCH_BY_SOURCE,
    PermissionCode.DEVELOPER_PRIVILEGES,
    PermissionCode.ACCESS_VLF,
    'manage_subscription',
    'admin_privileges',
    'access_dnb',
    'search_by_source',
    'developer_privileges',
    'access_vlf',
  ];

  showPermissionsChangeConfirm = false;

  override ngOnInit(): void {
    super.ngOnInit();

    // Keep navigation sidebar highlight on Subscription Plans
    const menuItem = this.menuService.getMenuItem(
      'configs/manage-subscription-plans',
    );
    if (menuItem) {
      this.menuService.menuOnChange(menuItem);
    }

    this.planNameParam =
      this.activatedRoute.snapshot.paramMap.get('plan_name') || '';
    this.isEditMode = !!this.planNameParam;

    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;

    forkJoin({
      permissions: this.permissionService.getAllCustomPermissions(1, 100),
      connections: this.subscriptionService.getSubscriptionConnections(1, 100),
      planDetail: this.isEditMode
        ? this.subscriptionService.getSubscriptionPlanDetail(this.planNameParam)
        : of(null),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          const rawPermissions = results.permissions.results || [];
          this.permissionList = rawPermissions.filter(
            (p) => !this.HIDDEN_PERMISSION_CODENAMES.includes(p.codename),
          );
          this.connectionsList = (results.connections.results || []).map(
            (conn) => ({
              ...conn,
              connection_display_name: conn.connection_display_name
                ? `${conn.connection_display_name} (${conn.connection_name})`
                : conn.connection_name,
            }),
          );

          if (this.isEditMode && results.planDetail) {
            this.selectedPlan = results.planDetail;
            this.populateEditForm(results.planDetail);
          }

          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: getApiErrorMessage(
              error,
              'Failed to load plan setup data.',
            ),
          });
          this.goBack();
        },
      });
  }

  private populateEditForm(plan: CustomSubscriptionPlan): void {
    this.createForm.plan_name.value = plan.plan_name;
    this.createForm.display_name.value = plan.display_name;

    const monthPrice = plan.prices?.find(
      (price) => price.billing_interval === 'month',
    );
    const yearPrice = plan.prices?.find(
      (price) => price.billing_interval === 'year',
    );

    this.createForm.month_price.value = monthPrice?.amount ?? null;
    this.createForm.year_price.value = yearPrice?.amount ?? null;

    this.createForm.trial_period_days.value = plan.trial_period_days ?? 0;

    // Permissions (filter out hidden permissions)
    this.createForm.permissions.value = (plan.permissions ?? [])
      .filter((sp) => !this.HIDDEN_PERMISSION_CODENAMES.includes(sp.codename))
      .map(
        (sp) =>
          this.permissionList.find((p) => p.id === sp.id) ||
          ({
            id: sp.id,
            name: sp.name,
            codename: sp.codename,
          } as AuthPermission),
      );

    // Connections and Admins
    const connectionNames = (plan.connections ?? []).map(
      (c) => c.connection_name,
    );
    this.createForm.connections.value = connectionNames;

    const adminMap: Record<string, number[]> = {};
    (plan.connections ?? []).forEach((c) => {
      const ids: number[] = [];
      if (c.subscription_admins && c.subscription_admins.length > 0) {
        c.subscription_admins.forEach((u) => {
          if (u.id && !ids.includes(u.id)) ids.push(u.id);
        });
      }
      const fromList = this.connectionsList.find(
        (item) => item.connection_name === c.connection_name,
      );
      if (
        fromList?.subscription_admins &&
        fromList.subscription_admins.length > 0
      ) {
        fromList.subscription_admins.forEach((u) => {
          if (u.id && !ids.includes(u.id)) ids.push(u.id);
        });
      }
      if (
        c.subscription_admin_user_id &&
        !ids.includes(c.subscription_admin_user_id)
      ) {
        ids.push(c.subscription_admin_user_id);
      }
      adminMap[c.connection_name] = ids;
    });
    this.connectionAdminSelections = adminMap;
  }

  goBack(): void {
    const state = window.history.state as {
      fromView?: boolean;
      returnUrl?: string;
    };

    if (this.isEditMode && this.planNameParam) {
      if (state?.fromView) {
        this.location.back();
      } else {
        this.router.navigate(
          ['/configs/manage-subscription-plans/view', this.planNameParam],
          {
            state: { returnUrl: state?.returnUrl },
          },
        );
      }
    } else {
      if (state?.returnUrl) {
        this.router.navigateByUrl(state.returnUrl);
      } else {
        this.router.navigate(['/configs/manage-subscription-plans']);
      }
    }
  }

  // Form Validations
  validatePlanName(val?: string): void {
    const rawValue =
      val !== undefined ? val : (this.createForm.plan_name.value ?? '');
    const trimmed = rawValue.trim();

    if (!trimmed) {
      this.createForm.plan_name.error = 'required';
      return;
    }

    const regex = /^[a-z0-9_]+$/;
    if (!regex.test(rawValue)) {
      this.createForm.plan_name.error = 'textValidation';
      return;
    }
    this.createForm.plan_name.error = null;
  }

  validateDisplayName(val?: string): void {
    const rawValue =
      val !== undefined ? val : (this.createForm.display_name.value ?? '');
    const value = rawValue.trim();
    if (!value) {
      this.createForm.display_name.error = 'required';
      return;
    }
    this.createForm.display_name.error = null;
  }

  onMonthlyPriceChange(val?: number | null): void {
    this.validateMonthlyPrice(val);
    const value = val !== undefined ? val : this.createForm.month_price.value;
    if (
      value !== null &&
      value !== undefined &&
      value !== ('' as unknown) &&
      !isNaN(Number(value)) &&
      Number(value) > 0
    ) {
      const yearly = Math.round(Number(value) * 12 * 100) / 100;
      this.createForm.year_price.value = yearly;
      this.validateYearlyPrice(yearly);
    } else if (
      value === null ||
      value === undefined ||
      value === ('' as unknown)
    ) {
      this.createForm.year_price.value = null;
      this.validateYearlyPrice(null);
    }
  }

  onMonthlyPriceBlur(): void {
    this.validateMonthlyPrice();
  }

  validateMonthlyPrice(val?: number | null): void {
    const value = val !== undefined ? val : this.createForm.month_price.value;
    if (value === null || value === undefined || value === ('' as unknown)) {
      this.createForm.month_price.error = 'required';
      return;
    }

    const numValue = Number(value);
    if (isNaN(numValue) || numValue <= 0) {
      this.createForm.month_price.error = 'greaterThanPrice';
      return;
    }

    if (numValue > 999999.99) {
      this.createForm.month_price.error = 'lessThanPrice';
      return;
    }

    this.createForm.month_price.error = null;
  }

  validateYearlyPrice(val?: number | null): void {
    const value = val !== undefined ? val : this.createForm.year_price.value;
    if (value !== null && value !== undefined && value !== ('' as unknown)) {
      const numValue = Number(value);
      if (isNaN(numValue) || numValue < 0) {
        this.createForm.year_price.error = 'greaterThanPrice';
        return;
      }
      if (numValue > 999999.99) {
        this.createForm.year_price.error = 'lessThanPrice';
        return;
      }
    }
    this.createForm.year_price.error = null;
  }

  validateTrialPeriodDays(val?: number | null): void {
    const value =
      val !== undefined ? val : this.createForm.trial_period_days.value;
    if (value === null || value === undefined || value < 0 || value > 365) {
      this.createForm.trial_period_days.error = 'range';
      return;
    }
    this.createForm.trial_period_days.error = null;
  }

  validatePermissions(): void {
    if (this.createForm.permissions.value.length === 0) {
      this.createForm.permissions.error = 'atleatOne';
      return;
    }
    this.createForm.permissions.error = null;
  }

  isPermissionChecked(permission: AuthPermission): boolean {
    return this.createForm.permissions.value.some(
      (p) => p.id === permission.id,
    );
  }

  togglePermission(permission: AuthPermission, checked: boolean): void {
    if (checked) {
      this.createForm.permissions.value.push(permission);
    } else {
      this.createForm.permissions.value =
        this.createForm.permissions.value.filter((p) => p.id !== permission.id);
    }
    this.validatePermissions();
  }

  isFormValid(): boolean {
    return (
      this.createForm.plan_name.error === null &&
      this.createForm.display_name.error === null &&
      this.createForm.month_price.error === null &&
      this.createForm.year_price.error === null &&
      this.createForm.trial_period_days.error === null &&
      this.createForm.permissions.error === null
    );
  }

  // Connection handling
  onApplyConnections(selectedValues: string[]): void {
    const prevAdmins = { ...this.connectionAdminSelections };
    this.createForm.connections.value = selectedValues;

    const nextAdmins: Record<string, number[]> = {};
    selectedValues.forEach((con) => {
      if (prevAdmins[con] !== undefined) {
        nextAdmins[con] = prevAdmins[con];
      } else {
        const connItem = this.connectionsList.find(
          (c) => c.connection_name === con,
        );
        const defaultAdmins =
          connItem?.subscription_admins?.map((u) => u.id) || [];
        nextAdmins[con] = defaultAdmins;
      }
    });
    this.connectionAdminSelections = nextAdmins;

    Object.keys(this.connectionUsersByName).forEach((con) => {
      if (!selectedValues.includes(con)) {
        delete this.connectionUsersByName[con];
      }
    });
  }

  getConnectionDisplayName(connectionName: string): string {
    const fromList = this.connectionsList?.find(
      (c) => c.connection_name === connectionName,
    );
    if (fromList) {
      return fromList.connection_display_name || fromList.connection_name;
    }

    const fromPlan = this.selectedPlan?.connections?.find(
      (c) => c.connection_name === connectionName,
    );
    return fromPlan?.display_name || connectionName;
  }

  getAdminUsers(connectionName: string): ConnectionUser[] {
    const adminIds = this.connectionAdminSelections[connectionName] || [];
    if (adminIds.length === 0) {
      return [];
    }

    const allKnownUsers: ConnectionUser[] = [
      ...(this.connectionUsersByName[connectionName] || []),
      ...(this.connectionsList?.find(
        (c) => c.connection_name === connectionName,
      )?.subscription_admins || []),
    ];

    const planConn = this.selectedPlan?.connections?.find(
      (c) => c.connection_name === connectionName,
    );
    if (planConn?.subscription_admins) {
      allKnownUsers.push(...planConn.subscription_admins);
    }
    if (
      planConn?.subscription_admin_user_id &&
      (planConn.subscription_admin_name || planConn.subscription_admin_email)
    ) {
      allKnownUsers.push({
        id: planConn.subscription_admin_user_id,
        first_name: planConn.subscription_admin_name || '',
        last_name: '',
        email: planConn.subscription_admin_email || '',
      });
    }

    const result: ConnectionUser[] = [];
    const seenIds = new Set<number>();

    for (const id of adminIds) {
      if (seenIds.has(id)) continue;
      seenIds.add(id);

      const matched = allKnownUsers.find((u) => u.id === id);
      if (matched) {
        result.push(matched);
      } else {
        result.push({
          id,
          first_name: `Admin #${id}`,
          last_name: '',
          email: '',
        });
      }
    }

    return result;
  }

  getAdminUserLabel(user: ConnectionUser): string {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || user.email || `Admin #${user.id}`;
  }

  getAdminTooltip(connectionName: string): string {
    const admins = this.getAdminUsers(connectionName);
    if (admins.length === 0) {
      return 'No admin assigned';
    }
    return admins.map((u) => this.getAdminUserLabel(u)).join(', ');
  }

  loadConnectionUsers(connectionName: string): void {
    this.isLoadingPickerUsers = true;
    this.subscriptionService
      .getConnectionUsers(connectionName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          this.connectionUsersByName = {
            ...this.connectionUsersByName,
            [connectionName]: users,
          };
          this.isLoadingPickerUsers = false;
        },
        error: (error) => {
          this.isLoadingPickerUsers = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: getApiErrorMessage(
              error,
              `Failed to load users for ${connectionName}.`,
            ),
          });
        },
      });
  }

  openAdminPicker(connectionName: string): void {
    this.adminPickerConnection = connectionName;
    this.tempAdminSelections = [
      ...(this.connectionAdminSelections[connectionName] || []),
    ];

    if (!this.connectionUsersByName[connectionName]) {
      this.loadConnectionUsers(connectionName);
    }
  }

  isPickerUserSelected(userId: number): boolean {
    return this.tempAdminSelections.includes(userId);
  }

  togglePickerUser(userId: number): void {
    if (this.tempAdminSelections.includes(userId)) {
      this.tempAdminSelections = this.tempAdminSelections.filter(
        (id) => id !== userId,
      );
    } else {
      this.tempAdminSelections = [...this.tempAdminSelections, userId];
    }
  }

  selectAllPickerUsers(): void {
    if (!this.adminPickerConnection) return;
    const users = this.connectionUsersByName[this.adminPickerConnection] || [];
    this.tempAdminSelections = users.map((u) => u.id);
  }

  clearPickerSelections(): void {
    this.tempAdminSelections = [];
  }

  confirmAdminSelection(): void {
    if (!this.adminPickerConnection) {
      return;
    }

    this.connectionAdminSelections = {
      ...this.connectionAdminSelections,
      [this.adminPickerConnection]: [...this.tempAdminSelections],
    };
    this.adminPickerConnection = null;
    this.tempAdminSelections = [];
  }

  cancelAdminSelection(): void {
    this.adminPickerConnection = null;
    this.tempAdminSelections = [];
  }

  buildConnectionPayloads(): PlanConnectionPayload[] {
    return this.createForm.connections.value.map((connectionName) => {
      const adminIds = this.connectionAdminSelections[connectionName] || [];
      return {
        connection_name: connectionName,
        subscription_admin_user_id: adminIds.length > 0 ? adminIds[0] : null,
        subscription_admin_user_ids: adminIds,
      };
    });
  }

  onSavePlan(): void {
    if (this.isEditMode) {
      this.validateDisplayName();
      this.validateTrialPeriodDays();
      this.validatePermissions();

      if (!this.isFormValid()) {
        return;
      }

      const currentPermissionIds = this.createForm.permissions.value
        .map((p) => p.id)
        .sort((a, b) => a - b);
      const originalPermissionIds = (this.selectedPlan?.permissions ?? [])
        .filter((sp) => !this.HIDDEN_PERMISSION_CODENAMES.includes(sp.codename))
        .map((p) => p.id)
        .sort((a, b) => a - b);
      const permissionsChanged =
        currentPermissionIds.length !== originalPermissionIds.length ||
        currentPermissionIds.some(
          (id, idx) => id !== originalPermissionIds[idx],
        );

      if (permissionsChanged && !this.showPermissionsChangeConfirm) {
        this.showPermissionsChangeConfirm = true;
        return;
      }

      const payload: UpdateSubscriptionPlanDetails = {};
      if (
        this.selectedPlan?.display_name !== this.createForm.display_name.value
      ) {
        payload.display_name = this.createForm.display_name.value;
      }

      payload.connections = this.buildConnectionPayloads();

      if (
        this.selectedPlan?.trial_period_days !==
        this.createForm.trial_period_days.value
      ) {
        payload.trial_period_days = this.createForm.trial_period_days.value;
      }

      if (permissionsChanged) {
        payload.permissions = currentPermissionIds;
      }

      this.showPermissionsChangeConfirm = false;
      this.isSaving = true;

      this.subscriptionService
        .updateCustomSubscriptionPlan(payload, this.createForm.plan_name.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isSaving = false;
            this.commonService.triggerToast({
              type: ToastType.Success,
              message: 'Successfully updated subscription plan.',
            });
            const returnUrl = (window.history.state as { returnUrl?: string })
              ?.returnUrl;
            this.router.navigate(
              [
                '/configs/manage-subscription-plans/view',
                this.createForm.plan_name.value,
              ],
              {
                replaceUrl: true,
                state: { returnUrl },
              },
            );
          },
          error: (err) => {
            this.isSaving = false;
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: getApiErrorMessage(
                err,
                'Failed to update subscription plan.',
              ),
            });
          },
        });
      return;
    }

    // Create Mode
    this.validatePlanName();
    this.validateDisplayName();
    this.validateMonthlyPrice();
    this.validateYearlyPrice();
    this.validatePermissions();
    this.validateTrialPeriodDays();

    if (!this.isFormValid()) {
      return;
    }

    const marketingFeatures = this.createForm.permissions.value.map((per) => ({
      name: per.name,
    }));

    const payload = {
      plan_name: this.createForm.plan_name.value.trim(),
      display_name: this.createForm.display_name.value.trim(),
      month_price: this.createForm.month_price.value,
      year_price: this.createForm.year_price.value,
      connections: this.buildConnectionPayloads(),
      permissions: this.createForm.permissions.value.map((p) => p.id),
      marketing_features: marketingFeatures,
      trial_period_days: this.createForm.trial_period_days.value,
    };

    this.isSaving = true;
    this.subscriptionService
      .createCustomSubscriptionPlan(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Successfully created subscription plan.',
          });
          const returnUrl = (window.history.state as { returnUrl?: string })
            ?.returnUrl;
          this.router.navigate(
            ['/configs/manage-subscription-plans/view', payload.plan_name],
            {
              replaceUrl: true,
              state: { returnUrl },
            },
          );
        },
        error: (err) => {
          this.isSaving = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: getApiErrorMessage(
              err,
              'Failed to create subscription plan.',
            ),
          });
        },
      });
  }

  onConfirmPermissionsChange(): void {
    this.onSavePlan();
  }
}
